<template>
  <b-modal :active.sync="isModalActive" :width="640" scroll="keep" @close="hide">
    <div class="flex flex-col border border-blue-light rounded shadow bg-white p-4">

      <div class="flex flex-col w-full">
        <div class="uppercase font-semibold text-blue" v-if="!this.id">{{ $t('add_measurement_title') }}</div>
        <div class="uppercase font-semibold text-blue" v-else>{{ $t('add_subresource') }}</div>
        <div class="text-blue">{{ $t('resource_data') }}</div>
      </div>
      <form @submit.prevent="submitForm">

        <div class="flex flex-col w-full mt-4">

          <div v-if="isBoqRootModeActive" class="flex flex-row mb-4">
            <div class="flex flex-col w-full">
              <div class="text-xs text-blue font-medium">{{ $t('boq_root_resource') }}</div>
              <select
                v-model="selectedBoqRootId"
                required
                :disabled="isLoadingBoqRoots"
                class="w-full h-8 bg-grey-lighter border border-grey-light rounded p-1 text-xs"
                @change="onBoqRootSelected"
              >
                <option value="" disabled>
                  {{ isLoadingBoqRoots ? $t('loading') + '...' : $t('select_boq_resource') }}
                </option>
                <option
                  v-for="root in boqRoots"
                  :key="'boq_root_' + root.id"
                  :value="root.id"
                >{{ root.code }} — {{ root.name }}</option>
              </select>
            </div>
          </div>

          <div class="flex flex-row">
            <div class="flex flex-col w-1/2 mr-2">
              <div class="text-xs text-blue font-medium">{{ $t("code") }}</div>
              <div class="flex flex-row w-full">
                <input
                  v-model="form.code"
                  title="code"
                  name="code"
                  :disabled="isBoqRootFieldsLocked"
                  class="w-full h-8 bg-grey-lighter border border-grey-light rounded p-1 text-xs"
                />
              </div>
            </div>

            <div class="flex flex-col w-1/2 mr-2" v-if="!isBoqRootModeActive">
              <div class="text-xs text-blue font-medium">{{ $t("existing_level_codes") }}</div>
              <div class="flex flex-row w-full">
                <select
                  class="w-full h-8 bg-grey-lighter border border-grey-light rounded p-1 text-xs"
                >
                  <option
                    v-for="option in siblingAssignments"
                    :value="option.id"
                    :key="'sibling_'+option.id"
                  >{{ option.code }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div class="flex flex-row mt-4">
            <div class="flex flex-col w-2/3">
              <div class="text-xs text-blue font-medium">{{ $t("name") }}</div>
              <div class="flex flex-row w-full">
                <input
                  v-model="form.name"
                  title="name"
                  required="true"
                  name="name"
                  :disabled="isBoqRootFieldsLocked"
                  class="w-full h-8 bg-grey-lighter border border-grey-light rounded p-1 text-xs"
                />
              </div>
            </div>
          </div>

          <div class="flex flex-row mt-4">

              <div class="w-1/4 flex flex-col">
                <div class="text-xs text-blue font-medium">{{ $t('measurement_unit')}}</div>
                <div class="flex flex-row w-full">
                  <select
                    v-model="form.measure_unit_id"
                    required="true"
                    :disabled="isMeasurementUnitsDisabled || isBoqRootFieldsLocked"
                    class="w-full h-8 bg-grey-lighter border border-grey-light rounded p-1 text-xs"
                  >
                  <option
                    v-for="option in unitsList"
                    :value="option.id"
                    :key="option.id"
                  >{{ option.name }}
                  </option>
                </select>
                </div>
              </div>

              <div class="w-1/4 flex flex-col mx-4">
                <div class="text-xs text-blue font-medium capitalize">{{ $t("units") }}</div>
                <div class="flex flex-row w-full items-center gap-2">
                  <input
                    v-model="form.total_units"
                    title="total_units"
                    required="true"
                    name="total_units"
                    type="number"
                    step="any"
                    min="0"
                    :max="boqAvailableUnits != null ? boqAvailableUnits : undefined"
                    :disabled="isBoqRootModeActive && !selectedBoqRootId"
                    class="w-full h-8 bg-grey-lighter border border-grey-light rounded p-1 text-xs"
                  />
                  <span
                    v-if="isBoqRootModeActive && boqAvailableUnits != null"
                    class="text-xs text-gray-600 whitespace-nowrap"
                  >{{ $t('boq_units_available', { amount: boqAvailableUnits }) }}</span>
                </div>
              </div>

              <div class="w-1/4 flex flex-col mr-2">
                <div class="text-xs text-blue font-medium">{{ $t("unit_price") }}</div>
                <div class="flex flex-row w-full">
                  <input
                    v-model="form.unit_price"
                    title="name"
                    required="true"
                    name="name"
                    class="w-full h-8 bg-grey-lighter border border-grey-light rounded p-1 text-xs"
                  />
                </div>
              </div>

              <div class="w-1/4 flex flex-col ml-2">
                <div class="text-xs text-blue font-medium">{{ $t("unit_cost") }}</div>
                <div class="flex flex-row w-full">
                  <input
                    v-model="form.unit_cost"
                    title="name"
                    required="true"
                    name="name"
                    class="w-full h-8 bg-grey-lighter border border-grey-light rounded p-1 text-xs"
                  />
                </div>
              </div>

          </div>

          <div class="flex flex-row mt-4">

              <div class="w-1/3 flex flex-col">
                <div class="text-xs text-blue font-medium capitalize">{{ $t("length") }}</div>
                <div class="flex flex-row w-full">

                  <input
                    v-model="form.length"
                    title="length"
                    name="length"
                    step=".01"
                    type="number"
                    class="w-full h-8 bg-grey-lighter border border-grey-light rounded p-1 text-xs"
                  />

                </div>
              </div>

              <div class="w-1/3 flex flex-col mx-4">
                <div class="text-xs text-blue font-medium capitalize">{{ $t("width") }}</div>
                <div class="flex flex-row w-full">
                  <input
                    v-model="form.width"
                    title="width"
                    name="width"
                    step=".01"
                    type="number"
                    class="w-full h-8 bg-grey-lighter border border-grey-light rounded p-1 text-xs"
                  />
                </div>
              </div>

              <div class="w-1/3 flex flex-col">
                <div class="text-xs text-blue font-medium capitalize">{{ $t("height") }}</div>
                <div class="flex flex-row w-full">
                  <input
                    v-model="form.height"
                    title="height"
                    name="height"
                    step=".01"
                    type="number"
                    class="w-full h-8 bg-grey-lighter border border-grey-light rounded p-1 text-xs"
                  />
                </div>
              </div>

          </div>

          <div class="flex flex-row mt-4">
            <div class="w-1/3 flex flex-col">
              <div class="text-xs text-blue font-medium">Vincular orden de pedido</div>
              <div class="flex flex-row w-full">
                <select
                  v-model="form.work_order_id"
                  class="w-full h-8 bg-grey-lighter border border-grey-light rounded p-1 text-xs"
                >
                  <option
                    v-for="option in orders"
                    :value="option.id"
                    :key="'order_'+option.id"
                  >{{ option.code }}
                  </option>
                </select>
              </div>
            </div>

            <div class="w-1/3 flex flex-col mx-4">
              <div class="text-xs text-blue font-medium capitalize">{{ $t("position") }}</div>
              <div class="flex flex-row w-full">

                <input
                  v-model="form.position"
                  title="position"
                  name="position"
                  class="w-full h-8 bg-grey-lighter border border-grey-light rounded p-1 text-xs"
                />

              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-row justify-end w-full mt-6 items-center">
          <a @click="hide()" class="text-blue hover:underline cursor-pointer mr-4">
            {{ $t('cancel') }}
          </a>
          <button class="btn btn-blue" type="submit" :disabled="isSaving">{{$t('save')}}</button>
        </div>
      </form>

    </div>
  </b-modal>
</template>

<script>
  import axios from "axios";
  import {mapGetters} from "vuex";

  export default {
    name: "assignment-modal",

    components: {},

    props: {
      entityId: {type: Number, required: true},
      entityType: {type: String, required: true},
    },

    data: () => ({
      id: null,
      isModalActive: false,
      isMeasurementUnitsDisabled: false,
      unitsList: [],
      orders: [],
      form: {},
      assignmentTypes: ["resource", "task"],
      siblingAssignments: [],
      isSaving: false,
      displayBoqRootMode: false,
      boqRootApiMode: null,
      boqParentActivityId: null,
      boqRoots: [],
      isLoadingBoqRoots: false,
      selectedBoqRootId: '',
    }),

    computed: {
      ...mapGetters({
        project: "app/project",
        company: "app/company"
      }),

      /** 'default' | 'root' | 'linked' según project.configs.boq_root
       * si es modo default, no cambia nada sigue como ahora 
       * root: en este caso se podran crear todos recursos de boq_root
       * linked: solo se podran crear recursos de boq_root si se ha vinculado a una actividad padre
       * unicamente esos recursos se mostraran en el modal de creacion de recursos
       */
      
      boqRootConfigMode() {
        const value = this.project && this.project.configs
          ? this.project.configs.boq_root
          : undefined

        if (value === 'root') {
          return 'root'
        }
        if (value === 'linked') {
          return 'linked'
        }
        return 'default'
      },

      isBoqRootModeActive() {
        if (!this.displayBoqRootMode || this.id) {
          return false
        }
        // comprobamos si el modo de boq_root es diferente a default tanto en el api como en la configuracion del proyecto
        const mode = this.boqRootApiMode || this.boqRootConfigMode
        return mode !== 'default'
      },

      isBoqRootFieldsLocked() {
        return this.isBoqRootModeActive
      },

      selectedBoqRoot() {
        if (!this.selectedBoqRootId) {
          return null
        }
        return this.boqRoots.find(
          (root) => String(root.id) === String(this.selectedBoqRootId)
        ) || null
      },

      boqAvailableUnits() {
        if (!this.isBoqRootModeActive || !this.selectedBoqRoot) {
          return null
        }
        const available = this.selectedBoqRoot.available_units
        return available != null ? Number(available) : null
      },
    },

    methods: {
      /**
       * Envia el "formulario" con una nueva medición
       */
      async submitForm() {
        if (this.isBoqRootModeActive && this.boqAvailableUnits != null) {
          const units = parseFloat(this.form.total_units)
          if (!Number.isNaN(units) && units > this.boqAvailableUnits) {
            this.$toast.open({
              message: this.$t('boq_units_available', { amount: this.boqAvailableUnits }),
              type: 'is-danger',
              position: 'is-top-right',
            })
            return
          }
        }

        this.isSaving = true;

        // Comprobamos si viene con el padre (submedicion)
        if(this.id != null){
          this.form.parent_assignment_id = this.id;
        }

        const payload = { ...this.form }
        // en modo boq_root, se debe enviar el id del boq_root seleccionado
        if (this.isBoqRootModeActive && this.selectedBoqRootId) {
          payload.boq_root_id = Number(this.selectedBoqRootId)
        } else {
          delete payload.boq_root_id
        }

        let url = "/api/v2/assignments/" + this.entityType + "/" + this.entityId;
        axios({
          method: "post",
          url: url,
          data: payload
        })
          .then(response => {
            this.$toast.open({
              message: this.$t("assignment_insert_success"),
              type: "is-success",
              position: "is-top-right"
            });

            this.isSaving = false;
            this.$emit("reload");
            this.hide();
          })
          .catch(error => {
            //Se produjo un error
            this.isSaving = false;
            if (error.response && error.response.status === 422) {
              //extraemos el primer error de validación
              error.error_msg =
                error.response.data.errors[
                  Object.keys(error.response.data.errors)[0]
                  ][0];
            }

            // Mostramos el error
            this.$toast.open({
              message: error.error_msg || this.$t("assignment_insert_error"),
              type: "is-danger",
              position: "is-top-right"
            });
          });
      },

      /**
       * Comprueba si los datos son de tipo recurso
       */
      isResource() {
        if (this.form.assignmentType == "resource") return true;

        return false;
      },

      /**
       *
       */
      isTask() {
      },

      /**
       * Pide la lista de unidades para el proyecto actual
       */
      getProjectUnits() {
        const {data} = axios
          // .get("/api/v2/project/" + this.project.id + "/measurement-units") // eliminar si tod ba bien
          .get("/api/v2/company/" + this.company.id + "/measurement-units")
          .then(response => {
            if (response.data && response.data.success) {
              this.unitsList = response.data.units;
            } else {
              this.$notify.error("error_loading_measurement_units_data");
            }
          });
      },

      async getWorkOrders() {
        let {data} = await axios.get('/api/v2/work-orders', {params: {
          project_id: this.project.id
        }});

        if (data.success) {
          this.orders = data.data
        } else {
          this.$notify.error("error_loading_data");
        }
      },

      onBoqRootSelected() {
        const selected = this.boqRoots.find(
          (root) => String(root.id) === String(this.selectedBoqRootId)
        )

        if (!selected) {
          this.form.code = ''
          this.form.name = ''
          this.form.measure_unit_id = null
          this.form.boq_root_id = null
          this.form.total_units = null
          return
        }

        this.form.code = selected.code || ''
        this.form.name = selected.name || ''
        this.form.measure_unit_id = selected.measure_unit_id
          || (selected.measure_unit && selected.measure_unit.id)
          || null
        this.form.boq_root_id = selected.id
        this.form.total_units = null
      },

      async fetchBoqRoots() {
        if (!this.displayBoqRootMode || !this.project || !this.project.id) {
          this.boqRoots = []
          this.boqRootApiMode = null
          this.boqParentActivityId = null
          return
        }

        this.isLoadingBoqRoots = true
        try {
          const { data } = await axios.get(
            `/api/v2/project/${this.project.id}/activity/${this.entityId}/boq-roots`
          )
          if (data.success) {
            this.boqRootApiMode = data.boq_root_mode
            this.boqParentActivityId = data.parent_activity_id
            this.boqRoots = data.boq_roots || []
          } else {
            this.boqRoots = []
          }
        } catch (error) {
          this.boqRoots = []
        } finally {
          this.isLoadingBoqRoots = false
        }
      },

      // pasado un listado de mediciones, genera el siguiente codigo de medicion (sumando 1 al ultimo numero): ej 002783.SE-CON-1060.05 -> 002783.SE-CON-1060.06
      generateCode(siblingAssignments) {
        const separators = /[._-]/; // separadores
        let siblingCodes = siblingAssignments.map(a => a.code).filter(Boolean);

        // Si no hay hermanos, comenzamos en 1
        if (!siblingCodes.length) return ".1";

        // Encontrar el mayor número final entre los códigos
        let maxNum = 0;
        let baseCode = "";

        siblingCodes.forEach(code => {
          const parts = code.split(separators);
          const last = parts[parts.length - 1];

          if (/^\d+$/.test(last)) {
            // Si es numero, comparo para ver cual es el maximo
            let num = parseInt(last, 10);
            if (num > maxNum) {
              maxNum = num;
              baseCode = code.substring(0, code.lastIndexOf(last));
            }
          } else {
            // Si ningún código termina en número, guardo el último base posible
            baseCode = code;
          }
        });

        // Si no había números, agregamos .1
        if (maxNum === 0 && !/^\d+$/.test(siblingCodes[0].split(separators).pop())) {
          return baseCode + (baseCode.match(/[._-]$/) ? "1" : ".1");
        }

        // Crear el próximo número con la misma longitud (para respetar ceros)
        const lastNum = siblingCodes[0].split(separators).pop();
        const width = /^\d+$/.test(lastNum) ? lastNum.length : 0;

        const nextNumber = String(maxNum + 1).padStart(width, "0");

        // Armar el nuevo código
        return baseCode + nextNumber;
      },

      /**
       * Abre este modal
       */
      show(assignment = null, siblingAssignments = [], options = {}) {
        this.displayBoqRootMode = options.displayBoqRootMode === true
        this.selectedBoqRootId = ''
        this.getProjectUnits();
        this.getWorkOrders();
        this.form = this.defaultFormValues();
        this.isModalActive = true;

        if(assignment){
          this.id = assignment.id;
          const unitId =
            (assignment.unit && assignment.unit.id != null)
              ? assignment.unit.id
              : assignment.measure_unit_id;
          this.form.measure_unit_id = unitId != null ? unitId : null;
          this.form.work_order_id = assignment.order?.id
          this.isMeasurementUnitsDisabled = true;
        }

        // si viene los hermanos, generamos el codigo automaticamente
        if(siblingAssignments.length > 0 && !this.isBoqRootModeActive) {
          this.form.code = this.generateCode(siblingAssignments);
          this.siblingAssignments = siblingAssignments;
        }

        if (this.displayBoqRootMode) {
          this.fetchBoqRoots()
        }
      },

      /**
       * Valores por defecto pra el form
       */
      defaultFormValues() {
        return {
          code: "",
          name: "",
          weight: "",
          assignmentType: "resource",
          measure_unit_id: null,
          parent_assignment_id: null,
          total_units: null,
          description: "",
          width: "",
          height: "",
          length: "",
          unit_price: 0,
          unit_cost: 0,
          boq_root_id: null,
        };
      },

      /**
       * Oculta este formulario
       */
      hide() {
        this.id = null;
        this.isModalActive = false;
        this.form = this.defaultFormValues();
        this.isMeasurementUnitsDisabled = false;
        this.siblingAssignments = [];
        this.isSaving = false;
        this.displayBoqRootMode = false;
        this.boqRootApiMode = null;
        this.boqParentActivityId = null;
        this.boqRoots = [];
        this.isLoadingBoqRoots = false;
        this.selectedBoqRootId = '';
      }
    }
  };
</script>
<style scoped>
  /* Oculta los botones para aumentar los inputs numéricos */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    /* display: none; <- Crashes Chrome on hover */
    -webkit-appearance: none;
    margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
  }

  input[type="number"] {
    -moz-appearance: textfield; /* Firefox */
  }
</style>
